import CancellableHelper from "@/requests/CancellableHelper";
import GenericTableRequest from "@/models/api/GenericTableRequest";
import { User } from "@/models/api/administration/users/User";
import { Office } from "@/models/api/administration/users/Office";
import { UserBasicInfo, UserBasicInfoResponse } from "@/models/api/administration/users/UserBasicInfo";
import { NewUser } from "@/models/api/administration/users/NewUser";
import { UserRole } from "@/models/api/administration/users/UserRole";
import { AgentCode } from "@/models/api/administration/users/AgentCode";
import { UserCashbox } from "@/models/api/administration/users/UserCashbox";

//#region Routes
export const URL_GET_USERS = "/user";
export const URL_GET_USER_OFFICES = "/user/${UserId}/Offices";
export const URL_GET_USER_OFFICES_TO_ADD = "/user/${UserId}/OfficesToAdd";
export const URL_GET_USER_BASIC_INFO = "/user/${UserId}/Office/${OfficeId}/Info";
export const URL_GET_USER_INFO = "/user/UserInfo";
export const URL_POST_EDIT_USER_BASIC_INFO = "/user/${UserId}/Office/${OfficeId}/EditInfo";
export const URL_POST_LOCK_USER = "/user/${UserId}/Lock";
export const URL_POST_UNLOCK_USER = "/user/${UserId}/Unlock";
export const URL_POST_ADD_USER_TO_OFFICE = "/user/${UserId}/Office/${OfficeId}/Add";
export const URL_POST_REMOVE_USER_FROM_OFFICE = "/user/${UserId}/Office/${OfficeId}/Remove";
export const URL_POST_ADD_USER = "/user/Add";
export const URL_POST_ADD_USER_ROLE = "/user/${UserId}/Office/${OfficeId}/Role/${RoleId}/Add";
export const URL_POST_REMOVE_USER_ROLE = "/user/${UserId}/Office/${OfficeId}/Role/${RoleId}/Remove";
export const URL_GET_UER_OFFICE_ROLES = "/user/${UserId}/Office/${OfficeId}/Role";
export const URL_GET_AGENT_CODES = "/user/${UserId}/Office/${OfficeId}/AgentCode";
export const URL_POST_ADD_AGENT_CODE = "/user/${UserId}/Office/${OfficeId}/AgentCode/Add";
export const URL_POST_EDIT_AGENT_CODE = "/user/${UserId}/Office/${OfficeId}/AgentCode/${AgentCodeId}/Edit";
export const URL_POST_REMOVE_AGENT_CODE = "/user/${UserId}/Office/${OfficeId}/AgentCode/${AgentCodeId}/Remove";
export const URL_GET_USER_CASH_BOXES = "/user/${UserId}/Office/${OfficeId}/Cashbox";
export const URL_POST_ADD_USER_PROFILE_CASH_BOX = "/user/${UserId}/Office/${OfficeId}/Cashbox/${CashboxId}/Add";
export const URL_POST_REMOVE_USER_PROFILE_CASH_BOX = "/user/${UserId}/Office/${OfficeId}/Cashbox/${CashboxId}/Remove";
export const URL_POST_ADD_DEFAULT_USER_PROFILE_CASH_BOX =
  "/user/${UserId}/Office/${OfficeId}/Cashbox/${CashboxId}/AddDefault";
export const URL_POST_CHANGE_PASSWORD = "/user/ChangePassword";
export const URL_POST_CHANGE_EMAIL = "/user/ChangeEmail";
export const URL_POST_CHANGE_CULTURE = "/user/ChangeCulture";
export const URL_POST_CHANGE_OFFICE_PROFILE = "/user/ChangeOfficeProfile";
export const URL_POST_CHANGE_UNCHECK_BOOKING_AFTER_EXIT = "/user/ChangeUncheckBookingAfterExit";
export const URL_POST_CHANGE_SYSTEM_DATE = "/user/SetSystemDate";
//#endregion

const _getUsersTable = new CancellableHelper<Array<User>>(URL_GET_USERS, User);
_getUsersTable.jsonSerializer = true;
export const getUsersTable = async (genericTableRequest: GenericTableRequest): Promise<Array<User>> => {
  return await _getUsersTable.get(genericTableRequest);
};

const _getUserOffices = new CancellableHelper<Array<Office>>(URL_GET_USER_OFFICES, Office);
export const getUserOffices = async (userId: string): Promise<Array<Office>> => {
  return await _getUserOffices.get({ UserId: userId });
};

const _getUserOfficesToAdd = new CancellableHelper<Array<Office>>(URL_GET_USER_OFFICES_TO_ADD, Office);
export const getUserOfficesToAdd = async (userId: string): Promise<Array<Office>> => {
  return await _getUserOfficesToAdd.get({ UserId: userId });
};

const _getUserBasicInfo = new CancellableHelper<UserBasicInfoResponse>(URL_GET_USER_BASIC_INFO, UserBasicInfoResponse);
export const getUserBasicInfo = async (userId: string, officeId: string): Promise<UserBasicInfoResponse> => {
  return await _getUserBasicInfo.get({ UserId: userId, OfficeId: officeId });
};

const _getUserInfo = new CancellableHelper<UserBasicInfoResponse>(URL_GET_USER_INFO, UserBasicInfoResponse);
export const getUserInfo = async (): Promise<UserBasicInfoResponse> => {
  return await _getUserInfo.get();
};

const _editUserBasicInfo = new CancellableHelper<null>(URL_POST_EDIT_USER_BASIC_INFO, null, true);
export const editUserBasicInfo = async (
  userId: string,
  officeId: string,
  userBasicInfo: UserBasicInfo
): Promise<null> => {
  return await _editUserBasicInfo.post({ UserId: userId, OfficeId: officeId }, userBasicInfo);
};

const _lockUser = new CancellableHelper<null>(URL_POST_LOCK_USER, null, true);
export const lockUser = async (userId: string): Promise<null> => {
  return await _lockUser.post({ UserId: userId }, null);
};

const _unlockUser = new CancellableHelper<null>(URL_POST_UNLOCK_USER, null, true);
export const unlockUser = async (userId: string): Promise<null> => {
  return await _unlockUser.post({ UserId: userId }, null);
};

const _addUserToOffice = new CancellableHelper<null>(URL_POST_ADD_USER_TO_OFFICE, null, true);
export const addUserToOffice = async (userId: string, officeId: string): Promise<null> => {
  return await _addUserToOffice.post({ UserId: userId, OfficeId: officeId }, null);
};

const _removeUserFromOffice = new CancellableHelper<null>(URL_POST_REMOVE_USER_FROM_OFFICE, null, true);
export const removeUserFromOffice = async (userId: string, officeId: string): Promise<null> => {
  return await _removeUserFromOffice.post({ UserId: userId, OfficeId: officeId }, null);
};

const _addUser = new CancellableHelper<string>(URL_POST_ADD_USER, undefined, false);
export const addUser = async (newUser: NewUser): Promise<string> => {
  return await _addUser.post({}, newUser);
};

const _addUserRole = new CancellableHelper<null>(URL_POST_ADD_USER_ROLE, null, true);
export const addUserRole = async (userId: string, officeId: string, roleId: string): Promise<null> => {
  return await _addUserRole.post({ UserId: userId, OfficeId: officeId, RoleId: roleId }, null);
};

const _removeUserRole = new CancellableHelper<null>(URL_POST_REMOVE_USER_ROLE, null, true);
export const removeUserRole = async (userId: string, officeId: string, roleId: string): Promise<null> => {
  return await _removeUserRole.post({ UserId: userId, OfficeId: officeId, RoleId: roleId }, null);
};

const _getUserOfficeRoles = new CancellableHelper<Array<UserRole>>(URL_GET_UER_OFFICE_ROLES, UserRole);
export const getUserOfficeRoles = async (userId: string, officeId: string): Promise<Array<UserRole>> => {
  return await _getUserOfficeRoles.get({ UserId: userId, OfficeId: officeId });
};

const _getAgentCodes = new CancellableHelper<Array<AgentCode>>(URL_GET_AGENT_CODES, AgentCode);
export const getAgentCodes = async (userId: string, officeId: string): Promise<Array<AgentCode>> => {
  return await _getAgentCodes.get({ UserId: userId, OfficeId: officeId });
};

const _addAgentCode = new CancellableHelper<null>(URL_POST_ADD_AGENT_CODE, null, true);
export const addAgentCode = async (
  userId: string,
  officeId: string,
  agentId: string,
  system: string
): Promise<null> => {
  return await _addAgentCode.post({ UserId: userId, OfficeId: officeId }, { AgentId: agentId, System: system });
};

const _editAgentCode = new CancellableHelper<null>(URL_POST_EDIT_AGENT_CODE, null, true);
export const editAgentCode = async (
  agentCodeId: number,
  userId: string,
  officeId: string,
  agentId: string,
  system: string
): Promise<null> => {
  return await _editAgentCode.post(
    { AgentCodeId: agentCodeId, UserId: userId, OfficeId: officeId },
    { AgentId: agentId, System: system }
  );
};

export const removeAgentCode = async (agentCodeId: number, userId: string, officeId: string): Promise<null> => {
  const _removeAgentCode = new CancellableHelper<null>(URL_POST_REMOVE_AGENT_CODE, null, true);
  return await _removeAgentCode.post({ AgentCodeId: agentCodeId, UserId: userId, OfficeId: officeId }, null);
};

const _getUserCashboxes = new CancellableHelper<Array<UserCashbox>>(URL_GET_USER_CASH_BOXES, UserCashbox);
export const getUserCashboxes = async (userId: string, officeId: string): Promise<Array<UserCashbox>> => {
  return await _getUserCashboxes.get({ UserId: userId, OfficeId: officeId });
};

const _addUserProfileCashbox = new CancellableHelper<null>(URL_POST_ADD_USER_PROFILE_CASH_BOX, null, true);
export const addUserProfileCashbox = async (userId: string, officeId: string, cashboxId: number): Promise<null> => {
  return await _addUserProfileCashbox.post({ UserId: userId, OfficeId: officeId, CashboxId: cashboxId }, null);
};

const _removeUserProfileCashbox = new CancellableHelper<null>(URL_POST_REMOVE_USER_PROFILE_CASH_BOX, null, true);
export const removeUserProfileCashbox = async (userId: string, officeId: string, cashboxId: number): Promise<null> => {
  return await _removeUserProfileCashbox.post({ UserId: userId, OfficeId: officeId, CashboxId: cashboxId }, null);
};

const _addUserProfileDefaultCashbox = new CancellableHelper<null>(
  URL_POST_ADD_DEFAULT_USER_PROFILE_CASH_BOX,
  null,
  true
);
export const addUserProfileDefaultCashbox = async (
  userId: string,
  officeId: string,
  cashboxId: number
): Promise<null> => {
  return await _addUserProfileDefaultCashbox.post({ UserId: userId, OfficeId: officeId, CashboxId: cashboxId }, null);
};

const _changePassword = new CancellableHelper<null>(URL_POST_CHANGE_PASSWORD, null, true);
export const changePassword = async (oldPassword: string, newPassword: string): Promise<null> => {
  return await _changePassword.post({
    OldPassword: oldPassword,
    NewPassword: newPassword,
  });
};

const _changeEmail = new CancellableHelper<null>(URL_POST_CHANGE_EMAIL, null, true);
export const changeEmail = async (email: string): Promise<null> => {
  return await _changeEmail.post({ Email: email });
};

const _changeCulture = new CancellableHelper<null>(URL_POST_CHANGE_CULTURE, null, true);
export const changeCulture = async (culture: string): Promise<null> => {
  return await _changeCulture.post({ Culture: culture });
};

const _changeOfficeProfile = new CancellableHelper<null>(URL_POST_CHANGE_OFFICE_PROFILE, null, true);
export const changeOfficeProfile = async (officeProfileId: number): Promise<null> => {
  return await _changeOfficeProfile.post({ OfficeProfileId: officeProfileId });
};

const _changeUncheckBookingAfterExit = new CancellableHelper<null>(
  URL_POST_CHANGE_UNCHECK_BOOKING_AFTER_EXIT,
  null,
  true
);
export const changeUncheckBookingAfterExit = async (uncheckBookingAfterExit: boolean): Promise<null> => {
  return await _changeUncheckBookingAfterExit.post({ UncheckBookingAfterExit: uncheckBookingAfterExit });
};

const _saveUserSystemTime = new CancellableHelper<null>(URL_POST_CHANGE_SYSTEM_DATE, null, true);
export const saveUserSystemDate = async (date: string): Promise<null> => {
  return await _saveUserSystemTime.post({ UserSystemDate: date });
};
